import React from "react";
import { Table, Dropdown } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { translations } from "../../utils/Translation/staticTranslation";
import ContactFilters from "./ContactFilters";
import "./Contacts.css";

const ContactsTableWithFilters = ({
  data,
  loading = false,
  searchValue = "",
  pagination = { current: 1, pageSize: 10, total: 0 },
  onSearchChange,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onCreateContactList,
  readOnly = false,
  canDelete = true,
}) => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.contactsPage || {};
  const isRTL = lang === "ar";

  const columns = [
    {
      title: t?.table?.name || "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text) => (
        <div className="contact-name-cell" style={{ fontWeight: 500, color: "#101828" }}>
          {text}
        </div>
      ),
    },
    {
      title: t?.table?.description || "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      render: (text) => (
        <div className="contact-description-cell" style={{ color: "#667085" }}>
          {text || "—"}
        </div>
      ),
    },
    {
      title: t?.table?.contacts_count || "Contacts Count",
      dataIndex: "contactsCount",
      key: "contactsCount",
      width: 150,
      render: (count) => (
        <div style={{ color: "#101828", fontWeight: 500 }}>{count || 0}</div>
      ),
    },
    {
      title: t?.table?.actions || "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const menuItems = [
          {
            key: "view",
            label: (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src="/Campaign/arrow-up-right.svg"
                  alt="View"
                  style={{ width: "16px", height: "16px" }}
                />
                {t?.buttons?.view || "View"}
              </span>
            ),
            onClick: () => onView && onView(record),
          },
          ...(!readOnly
            ? [
                {
                  key: "edit",
                  label: (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <img
                        src="/Campaign/Button-edit.svg"
                        alt="Edit"
                        style={{ width: "16px", height: "16px" }}
                      />
                      {t?.buttons?.edit || "Edit"}
                    </span>
                  ),
                  onClick: () => onEdit && onEdit(record),
                },
              ]
            : []),
          ...(!readOnly && canDelete
            ? [
                {
                  key: "delete",
                  label: (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <img
                        src="/Campaign/delete-icon.svg"
                        alt="Delete"
                        style={{ width: "16px", height: "16px" }}
                      />
                      {t?.buttons?.delete || "Delete"}
                    </span>
                  ),
                  onClick: () => onDelete && onDelete(record),
                },
              ]
            : []),
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <MoreOutlined className="contact-actions-icon" />
          </Dropdown>
        );
      },
    },
  ];

  const handleTableChange = (paginationInfo) => {
    if (onPageChange) {
      onPageChange(paginationInfo.current);
    }
  };

  return (
    <div className="contacts-table-container contacts-table-wrapper">
      <ContactFilters
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onCreateContactList={onCreateContactList}
        readOnly={readOnly}
      />
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          total: pagination.total,
          pageSize: pagination.pageSize,
          showSizeChanger: false,
          showLessItems: false,
          position: ["bottomRight"],
          style: {
            marginTop: "16px",
            marginBottom: "16px",
            textAlign: "right",
          },
          itemRender: (page, type, originalElement) => {
            const totalPages = Math.ceil(
              pagination.total / pagination.pageSize
            );

            if (type === "prev") {
              return (
                <a
                  onClick={(e) => {
                    if (isRTL) {
                      if (pagination.current < totalPages) {
                        e.preventDefault();
                        handleTableChange({ current: pagination.current + 1 });
                      }
                    } else {
                      if (pagination.current > 1) {
                        e.preventDefault();
                        handleTableChange({ current: pagination.current - 1 });
                      }
                    }
                  }}
                  style={{
                    color: isRTL
                      ? pagination.current >= totalPages
                        ? "#D0D5DD"
                        : "#18181B"
                      : pagination.current === 1
                      ? "#D0D5DD"
                      : "#18181B",
                    fontSize: "12px",
                    fontWeight: 400,
                    textDecoration: "none",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: isRTL
                      ? pagination.current >= totalPages
                        ? "not-allowed"
                        : "pointer"
                      : pagination.current === 1
                      ? "not-allowed"
                      : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    pointerEvents: isRTL
                      ? pagination.current >= totalPages
                        ? "none"
                        : "auto"
                      : pagination.current === 1
                      ? "none"
                      : "auto",
                  }}
                >
                  {isRTL ? (
                    <>
                      {t?.pagination?.previous || "Previous"}
                      <img
                        src="/Campaign/ChevronRight.svg"
                        alt="Previous"
                        style={{
                          width: "16px",
                          height: "16px",
                          transform: "scaleX(-1)",
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <img
                        src="/Campaign/ChevronLeft.svg"
                        alt="Previous"
                        style={{ width: "16px", height: "16px" }}
                      />
                      {t?.pagination?.previous || "Previous"}
                    </>
                  )}
                </a>
              );
            }
            if (type === "next") {
              return (
                <a
                  onClick={(e) => {
                    if (isRTL) {
                      if (pagination.current > 1) {
                        e.preventDefault();
                        handleTableChange({ current: pagination.current - 1 });
                      }
                    } else {
                      if (pagination.current < totalPages) {
                        e.preventDefault();
                        handleTableChange({ current: pagination.current + 1 });
                      }
                    }
                  }}
                  style={{
                    color: isRTL
                      ? pagination.current === 1
                        ? "#D0D5DD"
                        : "#18181B"
                      : pagination.current >= totalPages
                      ? "#D0D5DD"
                      : "#18181B",
                    fontSize: "12px",
                    fontWeight: 400,
                    textDecoration: "none",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: isRTL
                      ? pagination.current === 1
                        ? "not-allowed"
                        : "pointer"
                      : pagination.current >= totalPages
                      ? "not-allowed"
                      : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    pointerEvents: isRTL
                      ? pagination.current === 1
                        ? "none"
                        : "auto"
                      : pagination.current >= totalPages
                      ? "none"
                      : "auto",
                  }}
                >
                  {isRTL ? (
                    <>
                      <img
                        src="/Campaign/ChevronLeft.svg"
                        alt="Next"
                        style={{
                          width: "16px",
                          height: "16px",
                          transform: "scaleX(-1)",
                        }}
                      />
                      {t?.pagination?.next || "Next"}
                    </>
                  ) : (
                    <>
                      {t?.pagination?.next || "Next"}
                      <img
                        src="/Campaign/ChevronRight.svg"
                        alt="Next"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </>
                  )}
                </a>
              );
            }
            if (type === "page") {
              const isActive = page === pagination.current;
              return (
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    handleTableChange({ current: page });
                  }}
                  style={{
                    color: "#18181B",
                    backgroundColor: isActive ? "#F9FAFB" : "transparent",
                    padding: isActive ? "8px 14px" : "8px 14px",
                    borderRadius: isActive ? "8px" : "8px",
                    fontSize: "14px",
                    fontWeight: 400,
                    border: isActive
                      ? "1px solid #D0D5DD"
                      : "1px solid transparent",
                    textDecoration: "none",
                    display: "inline-block",
                    textAlign: "center",
                    cursor: "pointer",
                    minWidth: "36px",
                  }}
                >
                  {page}
                </a>
              );
            }
            if (type === "jump-prev" || type === "jump-next") {
              let targetPage;
              if (type === "jump-prev") {
                targetPage = Math.max(1, pagination.current - 5);
              } else {
                targetPage = Math.min(totalPages, pagination.current + 5);
              }

              return (
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    handleTableChange({ current: targetPage });
                  }}
                  style={{
                    color: "#18181B",
                    fontSize: "14px",
                    border: "none",
                    background: "transparent",
                    padding: "8px 14px",
                    cursor: "pointer",
                    display: "inline-block",
                    textAlign: "center",
                    minWidth: "36px",
                  }}
                >
                  ...
                </a>
              );
            }
            return originalElement;
          },
          onChange: (page, pageSize) => {
            handleTableChange({ current: page, pageSize });
          },
        }}
        rowKey="id"
      />
    </div>
  );
};

export default ContactsTableWithFilters;
